import {
  createShopApiGraphqlClient,
  type ShopApiGraphqlClient,
} from "../../../../packages/pvc-product/shop-api-transport";

export type PublicCatalogStatus =
  | "ACTIVE"
  | "COMING_SOON"
  | "QUOTE_ONLY";

export type PublicCatalogItem = {
  slug: string;
  name?: string;
  description?: string;
  featuredAssetPreview?: string | null;
  featuredAssetWidth?: number | null;
  featuredAssetHeight?: number | null;
  status: PublicCatalogStatus;
  displayOrder: number;
  homepageVisible: boolean;
  orderableOnline: boolean;
  quoteOnly: boolean;
  featured: boolean;
  bestseller: boolean;
};

const PUBLIC_CATALOG_QUERY = `
  query HappyPrintPublicCatalog {
    happyPrintPublicCatalog {
      slug
      name
      description
      featuredAssetPreview
      featuredAssetWidth
      featuredAssetHeight
      status
      displayOrder
      homepageVisible
      orderableOnline
      quoteOnly
      featured
      bestseller
    }
  }
`;

function record(value: unknown, path: string): Record<string, unknown> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function requiredString(value: unknown, path: string): string {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`${path} must be a non-empty string`);
  }
  return value;
}

function requiredBoolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw new Error(`${path} must be a boolean`);
  return value;
}

function requiredInteger(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) {
    throw new Error(`${path} must be an integer`);
  }
  return value;
}

function optionalPositiveInteger(value: unknown, path: string): number | null {
  if (value == null) return null;
  const parsed = requiredInteger(value, path);
  if (parsed <= 0) return null;
  return parsed;
}

export function decodePublicCatalog(data: unknown): PublicCatalogItem[] {
  const root = record(data, "data");
  if (!Array.isArray(root.happyPrintPublicCatalog)) {
    throw new Error("data.happyPrintPublicCatalog must be an array");
  }

  return root.happyPrintPublicCatalog.map((raw, index) => {
    const item = record(raw, `data.happyPrintPublicCatalog[${index}]`);
    const status = requiredString(
      item.status,
      `data.happyPrintPublicCatalog[${index}].status`,
    );
    if (!["ACTIVE", "COMING_SOON", "QUOTE_ONLY"].includes(status)) {
      throw new Error(`Unsupported public catalog status: ${status}`);
    }
    const slug = requiredString(
      item.slug,
      `data.happyPrintPublicCatalog[${index}].slug`,
    );
    return {
      slug,
      name:
        typeof item.name === "string" && item.name.trim()
          ? item.name
          : slug,
      description:
        typeof item.description === "string" ? item.description : "",
      featuredAssetPreview:
        item.featuredAssetPreview == null
          ? null
          : requiredString(
              item.featuredAssetPreview,
              `data.happyPrintPublicCatalog[${index}].featuredAssetPreview`,
            ),
      featuredAssetWidth: optionalPositiveInteger(
        item.featuredAssetWidth,
        `data.happyPrintPublicCatalog[${index}].featuredAssetWidth`,
      ),
      featuredAssetHeight: optionalPositiveInteger(
        item.featuredAssetHeight,
        `data.happyPrintPublicCatalog[${index}].featuredAssetHeight`,
      ),
      status: status as PublicCatalogStatus,
      displayOrder: requiredInteger(
        item.displayOrder,
        `data.happyPrintPublicCatalog[${index}].displayOrder`,
      ),
      homepageVisible: requiredBoolean(
        item.homepageVisible,
        `data.happyPrintPublicCatalog[${index}].homepageVisible`,
      ),
      orderableOnline: requiredBoolean(
        item.orderableOnline,
        `data.happyPrintPublicCatalog[${index}].orderableOnline`,
      ),
      quoteOnly: requiredBoolean(
        item.quoteOnly,
        `data.happyPrintPublicCatalog[${index}].quoteOnly`,
      ),
      featured: requiredBoolean(
        item.featured,
        `data.happyPrintPublicCatalog[${index}].featured`,
      ),
      bestseller: requiredBoolean(
        item.bestseller,
        `data.happyPrintPublicCatalog[${index}].bestseller`,
      ),
    };
  });
}

export function createPublicCatalogApi(client: ShopApiGraphqlClient) {
  return {
    async load(signal?: AbortSignal): Promise<PublicCatalogItem[]> {
      const data = await client.execute({
        operationName: "HappyPrintPublicCatalog",
        document: PUBLIC_CATALOG_QUERY,
        signal,
      });
      return decodePublicCatalog(data);
    },
  };
}

export function createBrowserPublicCatalogApi() {
  return createPublicCatalogApi(
    createShopApiGraphqlClient({
      endpoint: import.meta.env.VITE_SHOP_API_URL || "/shop-api",
    }),
  );
}
